import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import { addAppointmentToUser, updateAppointment } from '../../redux/slices/crmSlice';
import toast from 'react-hot-toast';
import CustomDatePicker from './CustomDatePicker';
import TimeSlotPicker from './TimeSlotPicker';

const AppointmentModal = ({ isOpen, onClose, user, editingAppointment }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        date: '',
        time: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset or populate form when modal opens
    useEffect(() => {
        if (isOpen) {
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';

            if (editingAppointment) {
                // Populate form with existing appointment data
                setFormData({
                    title: editingAppointment.title,
                    description: editingAppointment.description || '',
                    date: editingAppointment.date || (editingAppointment.dateTime ? editingAppointment.dateTime.split('T')[0] : ''),
                    time: editingAppointment.time || (editingAppointment.dateTime ? editingAppointment.dateTime.split('T')[1].substring(0, 5) : ''),
                });
            } else {
                // Set default to today at 09:00
                const today = new Date();
                const dateStr = today.toISOString().split('T')[0];
                
                setFormData({
                    title: '',
                    description: '',
                    date: dateStr,
                    time: '09:00',
                });
            }
        }

        // Cleanup: Re-enable body scroll when modal closes
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, editingAppointment]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim() || !formData.date || !formData.time) {
            toast.error('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);

        try {
            const appointmentData = {
                title: formData.title,
                description: formData.description,
                date: formData.date,
                time: formData.time,
                dateTime: `${formData.date}T${formData.time}`, // Keep consistency
            };

            if (editingAppointment) {
                // Update existing appointment
                await dispatch(
                    updateAppointment({
                        userId: user.id,
                        appointmentId: editingAppointment.id,
                        appointmentData: appointmentData,
                    }),
                ).unwrap();
                toast.success('Appointment updated successfully!');
            } else {
                // Create new appointment
                await dispatch(
                    addAppointmentToUser({
                        userId: user.id,
                        appointmentData: appointmentData,
                    }),
                ).unwrap();
                toast.success('Appointment scheduled successfully!');
            }

            onClose();
        } catch (error) {
            console.error('Error with appointment:', error);
            toast.error(
                editingAppointment
                    ? 'Failed to update appointment'
                    : 'Failed to schedule appointment',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className='fixed inset-0 z-[9999] flex items-end md:items-center justify-center md:p-4'>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        onClick={onClose}
                        className='absolute inset-0 bg-black/60 backdrop-blur-sm'
                    />

                    {/* Bottom Drawer (Mobile) / Center Modal (Desktop) */}
                    <motion.div
                        initial={{ y: '100%', opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: '100%', opacity: 0 }}
                        transition={{
                            type: 'spring',
                            damping: 30,
                            stiffness: 300,
                            mass: 0.8,
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className='relative w-full max-h-[90vh] md:max-h-[85vh] md:max-w-lg bg-white rounded-t-3xl md:rounded-2xl shadow-2xl border-t md:border border-slate-200 flex flex-col'
                        style={{ overscrollBehavior: 'contain' }}
                    >
                        {/* Drag Handle (Mobile Only) */}
                        <div className='md:hidden flex justify-center pt-3 pb-2'>
                            <div className='w-12 h-1.5 bg-slate-300 rounded-full' />
                        </div>

                        {/* Header */}
                        <div className='p-4 border-b border-slate-100 bg-slate-50'>
                            <div className='flex items-center justify-between'>
                                <div className='flex-1 pr-3'>
                                    <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                                        {editingAppointment ? 'Edit Appointment' : 'New Appointment'}
                                    </h3>
                                    <h2 className='text-lg md:text-xl font-black text-slate-900 mt-1'>
                                        {editingAppointment ? 'Update Details' : 'Schedule Meeting'}
                                    </h2>
                                    <p className='text-xs md:text-sm text-slate-500 font-medium mt-1 truncate'>
                                        with {user?.name} ({user?.number})
                                    </p>
                                </div>
                                <button
                                    onClick={onClose}
                                    type='button'
                                    className='p-2 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0'
                                >
                                    <XMarkIcon className='h-5 w-5 text-slate-500 hover:text-slate-900' />
                                </button>
                            </div>
                        </div>

                        {/* Form - Scrollable Content */}
                        <form onSubmit={handleSubmit} className='flex-1 overflow-y-auto'>
                            <div className='p-4 md:p-4 space-y-4'>
                                {/* Title */}
                                <div>
                                    <label
                                        htmlFor='title'
                                        className='block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2'
                                    >
                                        Title <span className='text-red-500'>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='title'
                                        name='title'
                                        value={formData.title}
                                        onChange={handleChange}
                                        placeholder='e.g., Initial Consultation'
                                        className='w-full px-3 md:px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm md:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-medium'
                                        required
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <label
                                        htmlFor='description'
                                        className='block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2'
                                    >
                                        Description
                                    </label>
                                    <textarea
                                        id='description'
                                        name='description'
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder='Add appointment details...'
                                        rows='3'
                                        className='w-full px-3 md:px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm md:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all resize-none font-medium'
                                    />
                                </div>

                                {/* Date and Time Pickers */}
                                <div className='grid grid-cols-2 gap-3 md:gap-4'>
                                    {/* Date Picker */}
                                    <div>
                                        <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2'>
                                            Date <span className='text-red-500'>*</span>
                                        </label>
                                        <CustomDatePicker
                                            value={formData.date}
                                            onChange={(value) => setFormData(prev => ({ ...prev, date: value }))}
                                            minDate={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>

                                    {/* Time Slot Picker */}
                                    <div>
                                        <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2'>
                                            Time <span className='text-red-500'>*</span>
                                        </label>
                                        <TimeSlotPicker
                                            value={formData.time}
                                            onChange={(value) => setFormData(prev => ({ ...prev, time: value }))}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Footer - Sticky at bottom */}
                            <div className='sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3 md:px-4 md:py-4 flex items-center justify-end gap-2 md:gap-3 shadow-lg'>
                                <button
                                    type='button'
                                    onClick={onClose}
                                    className='px-4 md:px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all'
                                >
                                    Cancel
                                </button>
                                <button
                                    type='submit'
                                    disabled={isSubmitting}
                                    className='px-4 md:px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-blue-600 text-white hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg'
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className='h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                                            <span className='hidden sm:inline'>
                                                {editingAppointment ? 'Updating...' : 'Scheduling...'}
                                            </span>
                                            <span className='sm:hidden'>
                                                {editingAppointment ? 'Update' : 'Schedule'}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <CalendarIcon className='h-4 w-4' />
                                            <span className='hidden sm:inline'>
                                                {editingAppointment
                                                    ? 'Update Appointment'
                                                    : 'Schedule Appointment'}
                                            </span>
                                            <span className='sm:inline md:hidden'>
                                                {editingAppointment ? 'Update' : 'Schedule'}
                                            </span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default AppointmentModal;
